<script lang="ts" context="module">
    import { persisted } from "svelte-local-storage-store"
    // Superset of store to make it save to LS
    const ONBOARDING_KEY = "onboardingSkipped"
    export const onboardingSkipped = persisted(ONBOARDING_KEY, false)
    // @ts-ignore
    window.onboardingEnable = () => onboardingSkipped.set(false)
</script>

<script lang="ts">
    // The logic for processing this is in
    // the pages/_layout file because routify has
    // an annoying limitation in that the $page
    // is passed down via context and so cannot
    // be accessed from anything basically outside
    // the /pages/ directory

    import { openAuth, registerView } from "@components/forms/Authenticate.svelte"
    import { fade } from "svelte/transition"
    import Button from "@components/button/Button.svelte"
    import Icon from "@components/material/Icon.svelte"

    type Screens = 1 | 2 | 3 | 4
    let screen: Screens = 1
    let screens: Screens[] = [1, 2, 3, 4]

    const text = {
        1: {
            image: "/images/bg/01.webp",
            title: "What is A Course in Miracles?",
            content: `<p><i>A Course in Miracles</i> is a spiritual path in which we find our way to God by distinguishing the real from the unreal. What is real is our true nature as complete and perfect spiritual beings. What is unreal is everything else: bodies, the world of form, and our judgments. In the act of forgiveness, we let go of the unreal, looking beyond appearances to the eternal holiness in everyone. If we can master forgiveness, we can find peace for ourselves, heal our relationships, and heal our world.</p>`,
        },
        2: {
            image: "/images/bg/02.webp",
            title: "What is the Complete & Annotated Edition?",
            content: `<p>The CE is an edition of <i>A Course in Miracles</i> that is based on the handwritten notes of scribe Helen Schucman. It retains the source material to the maximum degree possible, restoring approximately 45,000 words that were taken out in the original editing, and adding footnotes and cameo essays for the reader.</p>`,
        },
        3: {
            image: "/images/bg/03.webp",
            title: "How does the app work?",
            content: `<p>The CE app allows you to read, search, and listen to the Complete and Annotated Edition of <i>A Course in Miracles</i> wherever you are in the world at no charge. You can also set practice reminders to help you stay on track. We recommend you begin by registering as a user (this allows the app to remember where you left off), selecting a volume of study, and diving in! You will see helpful prompts throughout the app as you go.</p>`,
        },
        4: {
            image: "/images/bg/04.webp",
            title: "Access all the great features!",
            content: `<p>Having an account means all your saved locations, highlights, and notes are stored on the cloud and synced across devices.
                     You also have the option to opt in to our newsletter. It's all free!</p>`,
        },
    }

    function openAccountFlow(register: boolean) {
        $onboardingSkipped = true
        $registerView = register
        openAuth()
    }

    function goPrevious() {
        if (screen > 1) screen = (screen - 1) as Screens
    }

    function goNext() {
        if (screen < 4) screen = (screen + 1) as Screens
    }

    function finishOnboarding() {
        $onboardingSkipped = true
    }
</script>

<div class="onboarding-intro" transition:fade|global={{ duration: 180 }}>
    <div class="intro-shell">
        <section
            class="intro-card-shell themed-box"
            aria-label="Welcome to A Course in Miracles CE"
        >
            <div class="intro-card-header">
                {#key screen}
                    <img
                        src={text[screen].image}
                        alt=""
                        aria-hidden="true"
                        transition:fade|global={{ duration: 180 }}
                    />
                {/key}

                <div class="intro-brand">
                    <img src="/icons/pwa-512x512.png" alt="" aria-hidden="true" />
                    <div>
                        <span>Welcome</span>
                        <strong>A Course in Miracles CE</strong>
                    </div>
                </div>
            </div>

            <div class="intro-card-body">
                <section class="intro-copy" aria-live="polite">
                    <span class="intro-step">Part {screen} of {screens.length}</span>
                    <h2>{@html text[screen].title}</h2>
                    {@html text[screen].content}
                </section>

                <div class="intro-actions">
                    {#if screen === 4}
                        <div class="account-actions">
                            <Button
                                color="purple-soft"
                                fullwidth
                                icon="create-account-sm"
                                go={() => openAccountFlow(true)}
                            >
                                Create account
                            </Button>
                            <Button
                                color="white"
                                fullwidth
                                icon="key"
                                go={() => openAccountFlow(false)}
                            >
                                Sign in
                            </Button>
                        </div>
                    {/if}

                    <div class="pager-row" aria-label="Onboarding navigation">
                        <button
                            type="button"
                            class="nav-button previous"
                            disabled={screen === 1}
                            aria-label="Previous onboarding page"
                            on:click={goPrevious}
                        >
                            <span class="nav-icon"><Icon icon="arrow" /></span>
                        </button>

                        <div class="dots" aria-label="Onboarding pages">
                            {#each screens as i}
                                <button
                                    type="button"
                                    aria-label={`Page ${i}`}
                                    aria-current={i == screen ? "step" : undefined}
                                    on:click={() => (screen = i)}
                                    class="dots-dot"
                                    class:visible={i == screen}
                                />
                            {/each}
                        </div>

                        <button
                            type="button"
                            class="nav-button next"
                            disabled={screen === 4}
                            aria-label="Next onboarding page"
                            on:click={goNext}
                        >
                            <span>Next</span>
                            <span class="nav-icon"><Icon icon="arrow-right" /></span>
                        </button>
                    </div>

                    <Button color="purple" text go={finishOnboarding}>
                        {screen < 4 ? "Skip intro" : "Maybe later"}
                    </Button>
                </div>
            </div>
        </section>
    </div>
</div>

<style>
    .onboarding-intro {
        --intro-bg: var(--color-gold-100);
        --intro-text: var(--color-grey-900);
        --intro-muted: var(--color-grey-600);
        --intro-accent: var(--color-purple-700);
        position: fixed;
        inset: 0;
        z-index: 22;
        box-sizing: border-box;
        width: 100%;
        height: 100%;
        min-width: 0;
        min-height: 0;
        max-width: 100%;
        max-height: 100%;
        overflow: hidden;
        background: var(--intro-bg);
        color: var(--intro-text);
        isolation: isolate;
    }

    .onboarding-intro,
    .onboarding-intro * {
        box-sizing: border-box;
    }

    .onboarding-intro :global(header),
    .onboarding-intro :global(main),
    .onboarding-intro :global(footer) {
        display: block;
        grid-area: auto;
        width: auto;
        height: auto;
        min-height: 0;
        position: static;
    }

    :global(.darkmode) .onboarding-intro {
        --intro-bg: var(--color-purple-900);
        --intro-text: var(--color-grey-0);
        --intro-muted: var(--color-purple-100);
        --intro-accent: var(--color-gold-300);
    }

    .intro-shell {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        min-height: 0;
        margin: 0 auto;
        overflow: hidden;
        padding: calc(var(--safe-area-inset-top, 0rem) + 0.9rem)
            max(1rem, var(--safe-area-inset-right, 0rem))
            calc(var(--safe-area-inset-bottom, 0rem) + 0.9rem)
            max(1rem, var(--safe-area-inset-left, 0rem));
    }

    .intro-card-shell {
        display: flex;
        flex-direction: column;
        width: 100%;
        max-width: 56rem;
        height: 100%;
        min-width: 0;
        min-height: 0;
        overflow: hidden;
    }

    .intro-card-header {
        position: relative;
        flex: 0 0 36%;
        min-width: 0;
        min-height: 8rem;
        max-height: 18rem;
        overflow: hidden;
        background: var(--color-purple-50);
    }

    .intro-card-header::after {
        content: "";
        position: absolute;
        inset: 0;
        background: linear-gradient(
            180deg,
            rgb(41 34 74 / 0.08) 0%,
            rgb(41 34 74 / 0.22) 56%,
            rgb(41 34 74 / 0.68) 100%
        );
        pointer-events: none;
    }

    :global(.darkmode) .intro-card-header {
        background: var(--color-grey-900);
    }

    .intro-card-header > img {
        position: absolute;
        inset: 0;
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
    }

    .intro-brand {
        position: absolute;
        right: 1rem;
        bottom: 1rem;
        left: 1rem;
        z-index: 1;
        display: flex;
        align-items: center;
        gap: 0.75rem;
        min-width: 0;
        color: var(--color-grey-0);
    }

    .intro-brand img {
        flex: 0 0 auto;
        width: 3rem;
        height: 3rem;
        border-radius: var(--radius-button);
    }

    .intro-brand div {
        display: block;
        min-width: 0;
    }

    .intro-brand span,
    .intro-step {
        color: var(--intro-accent);
        font-size: 0.72rem;
        font-weight: 750;
        letter-spacing: 0;
        line-height: 1;
        text-transform: uppercase;
    }

    .intro-brand span {
        color: var(--color-gold-300);
    }

    .intro-brand strong {
        display: block;
        overflow: hidden;
        color: var(--color-grey-0);
        font-size: 1rem;
        font-weight: 750;
        line-height: 1.2;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .intro-card-body {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        gap: 1rem;
        min-width: 0;
        min-height: 0;
        overflow: hidden;
        padding: 1rem;
    }

    .intro-copy {
        display: block;
        flex: 1 1 auto;
        min-width: 0;
        min-height: 0;
        overflow-x: hidden;
        overflow-y: auto;
        overscroll-behavior: contain;
        touch-action: pan-y;
        -webkit-overflow-scrolling: touch;
        padding-right: 0.2rem;
    }

    .intro-step {
        color: var(--intro-accent);
        font-size: 0.72rem;
        font-weight: 750;
        letter-spacing: 0;
        line-height: 1;
        text-transform: uppercase;
    }

    h2 {
        margin: 0;
        color: var(--intro-text);
        font-family: Lora, ui-serif, Georgia, Cambria, "Times New Roman", Times, serif;
        font-size: 1.55rem;
        font-style: normal;
        font-weight: 500;
        letter-spacing: 0;
        line-height: 1.12;
        overflow-wrap: break-word;
    }

    .intro-step + h2 {
        margin-top: 0.8rem;
    }

    .intro-copy :global(p) {
        margin: 0.8rem 0 0;
        color: var(--intro-muted);
        font-size: 0.94rem;
        line-height: 1.58;
        overflow-wrap: break-word;
    }

    .intro-actions {
        display: flex;
        flex-direction: column;
        align-items: center;
        flex: 0 0 auto;
        gap: 0.7rem;
        width: 100%;
        min-width: 0;
    }

    .pager-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.5rem;
        width: 100%;
        min-width: 0;
    }

    .nav-button {
        appearance: none;
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
        flex: 0 1 auto;
        min-width: 0;
        max-width: 42%;
        min-height: 2.5rem;
        border: 1px solid var(--color-purple-100);
        border-radius: var(--radius-button);
        background: var(--color-purple-50);
        color: var(--color-purple-700);
        cursor: pointer;
        font: inherit;
        font-size: 0.9rem;
        font-weight: 650;
        line-height: 1;
        padding: 0.25rem 0.4rem;
        transition:
            transform 0.16s ease,
            border-color 0.16s ease,
            background-color 0.16s ease,
            color 0.16s ease,
            opacity 0.16s ease;
    }

    .nav-button span:not(.nav-icon) {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .nav-button.previous {
        margin-right: auto;
    }

    .nav-button.next {
        margin-left: auto;
    }

    .nav-button:hover {
        border-color: var(--color-purple-200);
        background: var(--color-purple-100);
    }

    .nav-button:active {
        transform: translateY(1px);
    }

    .nav-button:focus-visible {
        outline: 2px solid var(--color-gold-400);
        outline-offset: 2px;
    }

    .nav-button:disabled {
        cursor: default;
        opacity: 0.28;
        pointer-events: none;
    }

    .nav-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 2rem;
        height: 2rem;
        border-radius: var(--radius-button);
        background: var(--color-purple-100);
        color: var(--color-purple-700);
        flex: 0 0 auto;
    }

    .nav-button.next .nav-icon {
        background: var(--color-purple-700);
        color: var(--color-grey-0);
    }

    .nav-icon :global(svg) {
        width: 0.9rem;
        height: 0.9rem;
    }

    :global(.darkmode) .nav-button {
        border-color: var(--color-grey-700);
        background: var(--color-grey-800);
        color: var(--color-purple-100);
    }

    :global(.darkmode) .nav-button:hover {
        border-color: var(--color-grey-600);
        background: var(--color-grey-700);
        color: var(--color-grey-0);
    }

    :global(.darkmode) .nav-icon {
        background: var(--color-grey-700);
        color: var(--color-purple-100);
    }

    :global(.darkmode) .nav-button.next .nav-icon {
        background: var(--color-purple-500);
        color: var(--color-grey-0);
    }

    .dots {
        display: flex;
        flex: 0 0 auto;
        justify-content: center;
        gap: 0.55rem;
        width: auto;
    }

    .dots-dot {
        appearance: none;
        width: 0.55rem;
        aspect-ratio: 1 / 1;
        border: 0;
        border-radius: 0.22rem;
        background-color: var(--intro-accent);
        cursor: pointer;
        opacity: 0.42;
        padding: 0;
        transition:
            background-color 0.18s ease,
            opacity 0.18s ease,
            transform 0.18s ease;
    }

    .dots-dot.visible {
        background-color: var(--intro-accent);
        opacity: 1;
        transform: scale(1.12);
    }

    .dots-dot:focus-visible {
        outline: 2px solid var(--color-gold-400);
        outline-offset: 0.2rem;
    }

    .account-actions {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        width: 100%;
        max-width: 30rem;
        margin-inline: auto;
    }

    @media only screen and (min-width: 36rem) {
        .account-actions {
            flex-direction: row;
        }
    }

    @media only screen and (min-width: 46rem) {
        .intro-shell {
            padding: calc(var(--safe-area-inset-top, 0rem) + 1.5rem)
                max(2rem, var(--safe-area-inset-right, 0rem))
                calc(var(--safe-area-inset-bottom, 0rem) + 1.5rem)
                max(2rem, var(--safe-area-inset-left, 0rem));
        }

        .intro-card-body {
            gap: 1.2rem;
            padding: 1.5rem;
        }

        h2 {
            font-size: 2.1rem;
        }

        .intro-copy :global(p) {
            font-size: 1rem;
            line-height: 1.68;
        }
    }

    @media only screen and (min-width: 64rem) {
        .intro-shell {
            padding: calc(var(--safe-area-inset-top, 0rem) + 2rem)
                max(2.5rem, var(--safe-area-inset-right, 0rem))
                calc(var(--safe-area-inset-bottom, 0rem) + 2rem)
                max(2.5rem, var(--safe-area-inset-left, 0rem));
        }

        h2 {
            font-size: 2.35rem;
        }
    }

    @media only screen and (max-height: 38rem) {
        .intro-shell {
            padding: calc(var(--safe-area-inset-top, 0rem) + 0.55rem)
                max(0.75rem, var(--safe-area-inset-right, 0rem))
                calc(var(--safe-area-inset-bottom, 0rem) + 0.55rem)
                max(0.75rem, var(--safe-area-inset-left, 0rem));
        }

        .intro-card-header {
            flex-basis: 28%;
            min-height: 5.75rem;
        }

        .intro-brand {
            right: 0.75rem;
            bottom: 0.75rem;
            left: 0.75rem;
        }

        .intro-brand img {
            width: 2.35rem;
            height: 2.35rem;
            border-radius: 0.55rem;
        }

        .intro-card-body {
            gap: 0.55rem;
            padding: 0.8rem;
        }

        h2 {
            font-size: 1.35rem;
        }

        .intro-copy :global(p) {
            font-size: 0.88rem;
            line-height: 1.42;
        }

        .dots {
            gap: 0.45rem;
        }
        .account-actions {
            gap: 0.5rem;
        }
    }
</style>
